import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { InterwillCreateEntityOnSubmitType } from '@/_pages/interwill/hooks/useInterwillCreateEntity';
import { clientConfig } from '@/config/clientConfig';
import { SelectAssetCategory, SelectAssetOwnershipType } from '@/entities/assets';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import {
  AssetDto,
  NAME_KEY_BY_ASSET_CATEGORY,
  OwnershipType,
} from '@/shared/lib/sj-orm/models/asset.dto';

import {
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
} from '../styled';

import { InterwillIndirectAssetWarning } from './IndirectAssetWarning';
import { InterwillAssetFormParams } from './types';
import { InterwillAssetFormValueField } from './ValueField';

type InterwillAssetFormProps = {
  onSubmit: InterwillCreateEntityOnSubmitType;
};

export const InterwillAssetForm: FC<InterwillAssetFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addEntity.asset' });
  const isDesktop = useIsDesktop();

  const {
    control,
    handleSubmit: formHandleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<InterwillAssetFormParams>({
    defaultValues: {
      currency: clientConfig.defaultProfileBaseCurrency,
    },
  });

  const sjDatabase = useSJDatabase();

  const ownershipType = watch('ownerShipType');

  const handleSubmit = async (data: InterwillAssetFormParams) => {
    const nameKey = NAME_KEY_BY_ASSET_CATEGORY[data.category];

    const assetDto: AssetDto = {
      id: sjDatabase.generateId(CollectionName.ASSETS),
      category: data.category,
      [nameKey]: data.nickName,
      country: data.country,
      ownerShipType: data.ownerShipType,
      purchaseValue: {
        value: Number(data.value),
        from: data.currency,
      },
    };

    await onSubmit({ collection: CollectionName.ASSETS, dto: assetDto });
  };

  return (
    <>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer>
          <Controller
            name={'nickName'}
            control={control}
            rules={{ required: t(`fields.nickName.error`) }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.title`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'category'}
            control={control}
            rules={{ required: t(`fields.category.error`) }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectAssetCategory
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'country'}
            control={control}
            rules={{ required: t(`fields.country.error`) }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <FieldContainerIw required label={t(`fields.value.title`)}>
            <InterwillAssetFormValueField control={control} />
          </FieldContainerIw>

          <Controller
            name={'ownerShipType'}
            control={control}
            rules={{ required: t(`fields.ownerShipType.error`) }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectAssetOwnershipType
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          {ownershipType === OwnershipType.INDIRECT && <InterwillIndirectAssetWarning />}
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>
          <ButtonIw
            htmlType="submit"
            block={!isDesktop}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('submitButtonText')}
          </ButtonIw>
        </InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </>
  );
};
